// assembly reporting — a live recreation of ProductionReporting.xlsx (all 6 sheets), pulling
// from the Smartsheet-derived production report. sheets: MAIN (all-models daily log + team
// totals), the three per-team daily logs (GGO / ALPHAS / XPD), personel (headcounts), and boat
// stats (per-model boat counts, hours/boat, target date). colouring mirrors the workbook.
import { useMemo, useState, useEffect } from 'react';
import { MODEL_ORDER, teamLabel, teamOf, type ReportSummary, type Team } from '@technohull/shared';
import { useReport, useReportLog, useResetReportLog, type ReportLogEntry, type ReportLogMetrics } from '../features/hooks.js';
import { useAdminConfig, useSetConfig, type ReportMembers } from '../features/admin.js';
import { useAuth } from '../auth/AuthContext.js';
import { ErrorState, Fact, FactEnd, FactSentence, InlineError, LoadingState, Modal, PageHeader, SyncLabel } from '../components/ui.js';
import { useFormatDate } from '../lib/format.js';

const r0 = (n: number | null | undefined) => (n == null ? '-' : Math.round(n).toLocaleString('en-GB'));
const r1 = (n: number | null | undefined) => (n == null ? '-' : (Math.round(n * 10) / 10).toLocaleString('en-GB'));
const fmtMetric = (v: number | null | undefined) => {
  if (v == null || isNaN(v)) return '-';
  const a = Math.abs(v);
  if (a >= 100) return Math.round(v).toLocaleString('en-GB');
  if (a >= 1) return String(Math.round(v * 10) / 10);
  return String(Math.round(v * 10000) / 10000);
};
const pctClass = (p: number | null | undefined) =>
  p == null ? '' : p >= 100 ? 'wb-p100' : p >= 90 ? 'wb-p90' : p >= 70 ? 'wb-p70' : 'wb-plow';

// green when the day's rate of change meets-or-beats the ideal daily rate, red when behind — including
// "out of time with work still to do", where the ideal collapses to 0 and mustn't read as on-pace.
const dailyPaceClass = (rate: number | null | undefined, m: ReportLogMetrics | undefined): string => {
  const ideal = m?.dailyIdeal ?? null;
  if (rate == null || ideal == null) return '';
  if ((m?.daysAvail ?? 0) <= 0 && (m?.hoursNeeded ?? 0) > 0) return 'mes-rep-neg';
  return rate >= ideal ? 'mes-rep-pos' : 'mes-rep-neg';
};

// the 10 metric columns, in the workbook's exact order + labels.
const METRICS: Array<[string, keyof ReportLogMetrics]> = [
  ['HoursCompleted', 'hoursCompleted'],
  ['HRS Needed', 'hoursNeeded'],
  ['Days Avail.', 'daysAvail'],
  ['Theoretical HRS Available', 'theoretical'],
  ['Actual HRS 60%', 'actual60'],
  ['HRS Surplus', 'surplus'],
  ['Daily Rate of Change', 'dailyRate'],
  ['Daily Ideal Rate of Change', 'dailyIdeal'],
  ['Daily Hrs Completed', 'dailyDone'],
  ['Ideal Daily Hrs Completed', 'idealDone'],
];

// model columns per sheet (label = the workbook's header text; key = the app's model key).
interface Col { key: string; label: string }
const MAIN_MODELS: Col[] = MODEL_ORDER.map((m) => ({ key: m, label: m }));
const TEAM_SHEETS: Array<{ tab: string; team: Team; models: Col[] }> = [
  { tab: 'GT7-GTX-38-45-47', team: 'GGO', models: [
    { key: 'GT7', label: 'GT7' }, { key: 'GT9', label: 'GT9' }, { key: 'GTX', label: 'GTX' },
    // The source workbook headed this column "GS38", but the model is "38" everywhere else in the
    // app (MAIN sheet, Boat Stats, MODEL_ORDER); label it "38" so the tabs agree. Key stays '38'.
    { key: '38', label: '38' }, { key: '45', label: '45' }, { key: '47', label: '47' },
  ] },
  { tab: 'ALPHA 40-50', team: 'ALPHAS', models: [{ key: '40', label: '40' }, { key: '50', label: '50' }] },
  { tab: 'XPD', team: 'XPD', models: [{ key: 'XPD', label: 'XPD' }] },
];

// ── one daily-log sheet (MAIN or a team) ─────────────────────────────────────────
function LogSheet({
  rows, models, metricsOf, title,
}: {
  rows: ReportLogEntry[];
  models: Col[];
  metricsOf: (e: ReportLogEntry) => ReportLogMetrics | undefined;
  title: string;
}) {
  const fmtDate = useFormatDate();
  const pctFor = (e: ReportLogEntry, key: string): number | null => {
    const m = e.models.find((x) => x.model === key);
    return m ? m.pctDone : null;
  };
  return (
    <div className="mes-report">
      <div className="mes-report-hd">{title} · {rows.length} day{rows.length === 1 ? '' : 's'} (live, rolling)</div>
      {/* a class, not an inline max-height, so the phone rule can bound it to the screen instead */}
      <div className="mes-report-scroll wb-log-scroll">
        <table className="mes-report-table">
          <thead>
            <tr>
              <th className="pin-col">Date</th>
              {models.map((c) => <th key={c.key} className="wb-model">{c.label}</th>)}
              {METRICS.map(([l]) => <th key={l}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => {
              // dailyRate / dailyDone are day-over-day deltas filled in server-side (buildReportWithLog)
              // so Reporting and the Board's daily log always agree; here we just read + colour them.
              const met = metricsOf(e);
              return (
                <tr key={e.date}>
                  <td className="an-td-sap pin-col">{fmtDate(e.date)}</td>
                  {models.map((c) => {
                    const p = pctFor(e, c.key);
                    return <td key={c.key} className={`wb-modelcell ${pctClass(p)}`}>{p == null ? '' : Math.round(p) + '%'}</td>;
                  })}
                  {METRICS.map(([, k]) => {
                    const v = met ? met[k] : null;
                    // colour the two "are we on pace?" columns: HRS Surplus (green ≥0 / red <0) and the
                    // Daily Rate of Change (green on-or-above the ideal pace, red behind).
                    let cls = '';
                    if (k === 'surplus') cls = v == null ? '' : v < 0 ? 'mes-rep-neg' : 'mes-rep-pos';
                    else if (k === 'dailyRate') cls = dailyPaceClass(v, met);
                    return <td key={k} className={cls}>{fmtMetric(v)}</td>;
                  })}
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={1 + models.length + METRICS.length} style={{ textAlign: 'center', padding: 18 }}>No log rows yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── boat stats sheet ─────────────────────────────────────────────────────────────
// "Number of" is editable (admins): enter the total orders you PLAN for a model and TotalHRs
// projects planned × hrs/boat. Leave a cell blank to fall back to the live vessel count. The plan
// is stored app-side (config key report.plannedOrders) so it persists and everyone sees it.
function BoatStatsSheet({ report, canEdit }: { report: ReportSummary; canEdit: boolean }) {
  const fmtDate = useFormatDate();
  const save = useSetConfig();
  // planned counts ride along in the report (readable by everyone who can see it), so a non-admin
  // sees the same planned figures; only saving is admin-gated.
  const [planned, setPlanned] = useState<Record<string, number>>({});
  const [plannedHrs, setPlannedHrs] = useState<Record<string, number>>({});
  // reseed only when the SAVED plan actually changes (by value), so a background report refetch that
  // returns the same plan can't wipe an admin's in-progress edits.
  const savedKey = JSON.stringify([report.plannedOrders ?? {}, report.plannedHours ?? {}]);
  useEffect(() => {
    const [o, h] = JSON.parse(savedKey) as [Record<string, number>, Record<string, number>];
    setPlanned(o);
    setPlannedHrs(h);
  }, [savedKey]);

  // clamp to the server's bounds so a fat-fingered value can't be typed then rejected with a 400.
  const editMap = (
    setter: React.Dispatch<React.SetStateAction<Record<string, number>>>,
    key: string,
    raw: string,
    integer: boolean,
    max: number,
  ) =>
    setter((p) => {
      const next = { ...p };
      const t = raw.trim();
      if (t === '') delete next[key];
      else { const n = integer ? Math.floor(Number(t)) : Number(t); if (!Number.isNaN(n) && n >= 0) next[key] = Math.min(max, n); }
      return next;
    });
  // key-order-independent compare so re-entering the same value doesn't falsely mark the plan dirty.
  const canon = (o: Record<string, number>) =>
    JSON.stringify(Object.entries(o).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
  const dirty = canon(planned) !== canon(report.plannedOrders ?? {}) || canon(plannedHrs) !== canon(report.plannedHours ?? {});

  const byModel = new Map(report.models.map((m) => [m.model, m]));
  const rows = MODEL_ORDER.map((key) => {
    const m = byModel.get(key);
    const actual = m?.vessels ?? 0;
    const actualTotal = m ? (m.hoursCompleted ?? 0) + (m.hoursNeeded ?? 0) : 0;
    const derivedHpb = actual ? Math.round(actualTotal / actual) : 0;
    const hrsPerBoat = plannedHrs[key] != null ? plannedHrs[key]! : derivedHpb;
    const overridden = planned[key] != null || plannedHrs[key] != null;
    const num = planned[key] != null ? planned[key]! : actual;
    // once anything is planned, project count × hrs/boat; otherwise show the live total from Smartsheet.
    const total = overridden ? num * hrsPerBoat : actualTotal;
    return { key, actual, num, hrsPerBoat, total, tag: teamOf(key), overridden };
  }).filter((r) => canEdit || r.actual > 0 || r.total > 0 || r.overridden);

  // keep the team order the same as everywhere else (the TEAMS constant / Personel sheet).
  const teams: Team[] = ['GGO', 'ALPHAS', 'XPD'];
  const teamTot = (t: Team) => {
    const rs = rows.filter((r) => r.tag === t);
    const boats = rs.reduce((s, r) => s + r.num, 0);
    const hrs = rs.reduce((s, r) => s + r.total, 0);
    const members = report.groups.find((g) => g.team === t)?.members ?? 0;
    return { boats, hrs, members };
  };
  const totBoats = rows.reduce((s, r) => s + r.num, 0);
  const totHrs = rows.reduce((s, r) => s + r.total, 0);
  const totMembers = report.groups.reduce((s, g) => s + g.members, 0);

  return (
    <div className="wb-grid2">
      <div className="mes-report">
        <div className="mes-report-hd" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>BOAT STATS · per-model build hours</span>
          {canEdit && (
            <>
              <span style={{ flex: 1 }} />
              {save.isSuccess && !dirty && <span className="muted" style={{ fontSize: 11 }}>Saved</span>}
              <button className="btn btn-sm btn-primary" disabled={!dirty || save.isPending}
                onClick={() => save.mutate({ reportPlannedOrders: planned, reportPlannedHrs: plannedHrs })}>
                {save.isPending ? 'Saving…' : 'Save plan'}
              </button>
            </>
          )}
        </div>
        {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
        <div className="mes-report-scroll">
          <table className="mes-report-table">
            <thead><tr><th>Model</th><th>Number of</th><th>hrs per boat</th><th>TotalHRs</th><th>Tag</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td className="an-td-sap">{r.key}</td>
                  <td>
                    {canEdit ? (
                      <input
                        className="field wb-num"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={9999}
                        value={planned[r.key] ?? ''}
                        placeholder={String(r.actual)}
                        title={`Planned orders - blank uses the live count (${r.actual})`}
                        onChange={(e) => editMap(setPlanned, r.key, e.target.value, true, 9999)}
                      />
                    ) : (
                      r.num
                    )}
                  </td>
                  <td>
                    {canEdit ? (
                      <input
                        className="field wb-num wb-num--hpb"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        max={99999}
                        value={plannedHrs[r.key] ?? ''}
                        placeholder={r.actual ? String(r.hrsPerBoat) : '-'}
                        title="hrs per boat - blank uses the live average (TotalHRs ÷ live boats); set it to project a model with no boats yet"
                        onChange={(e) => editMap(setPlannedHrs, r.key, e.target.value, false, 99999)}
                      />
                    ) : (
                      r0(r.hrsPerBoat)
                    )}
                  </td>
                  <td>
                    {r.overridden && r.hrsPerBoat === 0 ? (
                      <span className="muted" title="No hrs/boat yet - a boat of this model has to be in production first before its planned hours can be projected.">-</span>
                    ) : (
                      r0(r.total)
                    )}
                  </td>
                  <td>{r.tag}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mes-report">
        <div className="mes-report-hd">TOTALS · by team{report.targetDate ? ` · target ${fmtDate(report.targetDate)}` : ''}</div>
        <div className="mes-report-scroll">
          <table className="mes-report-table">
            <thead><tr><th>Team</th><th>Boats</th><th>TotalHRs</th><th>Members</th></tr></thead>
            <tbody>
              {teams.map((t) => { const x = teamTot(t); return (
                <tr key={t}><td className="an-td-sap" title={t}>{teamLabel(t)}</td><td>{x.boats}</td><td>{r0(x.hrs)}</td><td>{x.members}</td></tr>
              ); })}
              <tr className="mes-rep-total"><td className="an-td-sap">Totals</td><td>{totBoats}</td><td>{r0(totHrs)}</td><td>{totMembers}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── personel sheet (team headcounts; editable by admins) ─────────────────────────
function PersonelSheet({ report, canEdit }: { report: ReportSummary; canEdit: boolean }) {
  const { data } = useAdminConfig();
  const save = useSetConfig();
  const [members, setMembers] = useState<ReportMembers>({ GGO: 0, ALPHAS: 0, XPD: 0 });
  const [targetDate, setTargetDate] = useState('');
  useEffect(() => { if (data?.reportMembers) setMembers(data.reportMembers); }, [data]);
  useEffect(() => { if (data) setTargetDate(data.reportTargetDate ?? ''); }, [data]);

  const teams: Team[] = ['GGO', 'ALPHAS', 'XPD'];
  const total = report.groups.reduce((s, g) => s + g.members, 0);

  return (
    <div className="wb-grid2">
      <div className="mes-report">
        <div className="mes-report-hd">PERSONEL · team headcounts (drive theoretical capacity)</div>
        <div className="mes-report-scroll">
          <table className="mes-report-table">
            <thead><tr><th>Team</th><th>Members</th><th>Capacity (hrs/day @60%)</th></tr></thead>
            <tbody>
              {teams.map((t) => { const g = report.groups.find((x) => x.team === t); const mm = g?.members ?? 0; return (
                <tr key={t}><td className="an-td-sap" title={t}>{teamLabel(t)}</td><td>{mm}</td><td>{r1(mm * 8 * 0.6)}</td></tr>
              ); })}
              <tr className="mes-rep-total"><td className="an-td-sap">Total</td><td>{total}</td><td>{r1(total * 8 * 0.6)}</td></tr>
            </tbody>
          </table>
        </div>
        <p className="wb-note" style={{ marginTop: 8 }}>The staff roster (names per task group) lives in the source workbook; this reflects the live headcounts used by the report.</p>
      </div>

      {canEdit && (
        <div className="page-panel" style={{ marginBottom: 0 }}>
          <h2 className="page-title" style={{ fontSize: 13, marginBottom: 4 }}>Edit report settings</h2>
          <p className="muted" style={{ marginTop: 0 }}>Headcounts drive theoretical capacity (members × 8h × 60%); the target date drives days-available &amp; surplus.</p>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block' }}>
              <span className="field-label">Report target date</span>
              <input className="field" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
            </label>
            <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>Leave blank to use the default season end (14 Aug).</div>
          </div>

          {(['GGO', 'ALPHAS', 'XPD'] as const).map((team) => (
            <div key={team} style={{ marginBottom: 10 }}>
              <label style={{ display: 'block' }}>
                <span className="field-label" title={team}>{teamLabel(team)}</span>
                <input className="field" type="number" min={0} value={members[team]} onChange={(e) => setMembers((m) => ({ ...m, [team]: Number(e.target.value) }))} />
              </label>
            </div>
          ))}
          <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate({ reportMembers: members, reportTargetDate: targetDate })}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {save.isSuccess && <span className="muted" style={{ marginLeft: 10 }}>Saved.</span>}
          {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
        </div>
      )}
    </div>
  );
}

type Tab = 'MAIN' | 'GT7-GTX-38-45-47' | 'ALPHA 40-50' | 'XPD' | 'Personel' | 'Boat Stats';
const TABS: Tab[] = ['MAIN', 'GT7-GTX-38-45-47', 'ALPHA 40-50', 'XPD', 'Personel', 'Boat Stats'];

export function Reporting() {
  const report = useReport();
  const reportLog = useReportLog();
  const resetLog = useResetReportLog();
  const { can } = useAuth();
  const fmtDate = useFormatDate();
  const [tab, setTab] = useState<Tab>('MAIN');
  const [showRebuild, setShowRebuild] = useState(false);
  const data = report.data;
  const syncing = report.isFetching || reportLog.isFetching;
  // "keep previous data" → just recompute today's row live and leave the history in place.
  const rebuildKeep = () => {
    void report.refetch();
    void reportLog.refetch();
    setShowRebuild(false);
  };
  // "delete all" → wipe the season's stored daily history, then rebuild from today's live figures.
  const rebuildDelete = () => {
    resetLog.mutate(undefined, { onSuccess: () => setShowRebuild(false) });
  };

  // merge the persisted rolling log with today's live row, newest-first.
  const rows = useMemo(() => {
    const byDate = new Map<string, ReportLogEntry>();
    for (const e of reportLog.data?.log ?? []) byDate.set(e.date, e);
    if (reportLog.data?.today) byDate.set(reportLog.data.today.date, reportLog.data.today);
    return [...byDate.values()].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [reportLog.data]);

  return (
    <>
      <PageHeader title="Assembly Reporting">
        {data && <span className="muted">Live from Smartsheet{data.targetDate ? ` · target ${fmtDate(data.targetDate)}` : ''}</span>}
        <button className="btn btn-sm" disabled={syncing} onClick={() => { void report.refetch(); void reportLog.refetch(); }}><SyncLabel busy={syncing} /></button>
        {can('admin:config') && (
          <button className="btn btn-sm" disabled={resetLog.isPending} onClick={() => setShowRebuild(true)} title="Recompute today's row live; optionally clear the stored daily history first">
            {resetLog.isPending ? 'Rebuilding…' : 'Rebuild log (from today)'}
          </button>
        )}
      </PageHeader>

      {(report.isLoading || reportLog.isLoading) && <LoadingState label="Computing report from Smartsheet…" />}
      {report.error && <ErrorState error={report.error} onRetry={() => void report.refetch()} />}
      {!report.error && reportLog.error && <ErrorState error={reportLog.error} onRetry={() => void reportLog.refetch()} />}

      {data && (
        <>
          {/* the headline: the report's eight figures as two sentences, each in its status colour */}
          <FactSentence className="wb-facts">
            The build{' '}
            {data.buildPct != null
              ? <>is <Fact tone="acc" title="Fleet build completion from the manpower & hours report">{data.buildPct}% complete</Fact> with</>
              : 'has'}{' '}
            <Fact title="Labour-hours still to do across the fleet">{r0(data.hoursLeft)} {data.hoursLeft === 1 ? 'hour' : 'hours'}</Fact> left<FactEnd>,</FactEnd>{' '}
            <Fact title="Working days (Mon-Fri, plus the workbook's 3) until the target date">{data.daysAvail} working {data.daysAvail === 1 ? 'day' : 'days'}</Fact> available{' '}
            and <Fact title={`Calendar days until the target${data.targetDate ? `, ${fmtDate(data.targetDate)}` : ''}`}>{data.daysToTarget} {data.daysToTarget === 1 ? 'day' : 'days'}</Fact> to target<FactEnd>.</FactEnd>{' '}
            <Fact tone={data.teamsOk < data.teamsN ? 'warn' : 'ok'} title="Teams whose 60% capacity covers the hours needed by the target date">{data.teamsOk} of {data.teamsN} teams</Fact>{' '}
            {data.teamsOk === 1 ? 'is' : 'are'} on track, with{' '}
            {data.overdue
              ? <Fact tone="err" title="Boats past their planned delivery date">{data.overdue} {data.overdue === 1 ? 'boat' : 'boats'} overdue</Fact>
              : <Fact tone="ok" title="Boats past their planned delivery date">no boats overdue</Fact>}<FactEnd>,</FactEnd>{' '}
            <Fact tone={data.onHold ? 'warn' : undefined} title="Boats with a Hold flag">{data.onHold} on hold</Fact>{' '}
            and <Fact tone={data.delivered ? 'ok' : undefined} title="Boats confirmed delivered in the Overview">{data.delivered} delivered</Fact><FactEnd>.</FactEnd>
          </FactSentence>

          <div className="wb-tabs" role="tablist" aria-label="Workbook sheet">
            {TABS.map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} className={`wb-tab ${tab === t ? 'on' : ''}`}
                // phones scroll the tab strip sideways; keep the picked sheet's tab in view
                onClick={(e) => { setTab(t); e.currentTarget.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' }); }}>{t}</button>
            ))}
          </div>

          {tab === 'MAIN' && (
            <LogSheet title="MAIN" rows={rows} models={MAIN_MODELS} metricsOf={(e) => e.all} />
          )}
          {TEAM_SHEETS.filter((s) => s.tab === tab).map((s) => (
            <LogSheet key={s.tab} title={s.tab} rows={rows} models={s.models} metricsOf={(e) => e.groups.find((g) => g.team === s.team) ?? e.all} />
          ))}
          {tab === 'Personel' && <PersonelSheet report={data} canEdit={can('admin:config')} />}
          {tab === 'Boat Stats' && <BoatStatsSheet report={data} canEdit={can('admin:config')} />}
        </>
      )}

      {showRebuild && (
        <Modal
          title="Rebuild log (from today)"
          onClose={() => setShowRebuild(false)}
          footer={
            <>
              <button className="btn btn-ghost" disabled={resetLog.isPending} onClick={() => setShowRebuild(false)}>Cancel</button>
              <span className="wb-ft-sp" />
              <button className="btn" disabled={resetLog.isPending} onClick={rebuildKeep}>Keep previous data</button>
              <button className="btn btn-danger" disabled={resetLog.isPending} onClick={rebuildDelete}>
                {resetLog.isPending ? 'Deleting…' : 'Delete all'}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            Today's row is always recomputed live from Smartsheet, so the current figures are correct either way.
            The choice is what happens to the stored daily history for this season:
          </p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 18, lineHeight: 1.5 }}>
            <li><b>Keep previous data</b> - refresh today's row and leave the earlier days as they are. Any rows recorded before a calculation fix stay as they were on that day.</li>
            <li><b>Delete all</b> - clear every stored daily row for this season, then start again from today.</li>
          </ul>
          <p className="wb-note" style={{ margin: 0, color: 'var(--err-fg)' }}>
            <b>Warning:</b> Delete all can't be undone. Past days can't be recomputed - the numbers were snapshots of Smartsheet on each of those days, and only the current state is available. This removes the daily‑trend history only; it never touches Smartsheet, boats, or the live totals.
          </p>
          {resetLog.isError && <InlineError error={resetLog.error} onDismiss={() => resetLog.reset()} />}
        </Modal>
      )}
    </>
  );
}
